import { DialogContent } from '../../components/ui/DialogContent';
import { useState, useEffect } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogActions } from '../../components/ui/DialogActions';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import {
  usernameService,
  validateUsernameFormat,
  getRemainingCooldownDays,
  USERNAME_COOLDOWN_DAYS,
} from '../../services/usernameService';

interface ChangeUsernameModalProps {
  embedded?: boolean;
  onDismiss?: () => void;
  open: boolean;
  onClose: () => void;
  userId: string;
  usernameUpdatedAt?: Date | null;
  onUsernameChanged: (newUsername: string) => void;
}

export function ChangeUsernameModal({
  open,
  embedded = false,
  onDismiss,
  onClose,
  userId,
  usernameUpdatedAt,
  onUsernameChanged,
}: ChangeUsernameModalProps) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const remainingCooldownDays = getRemainingCooldownDays(usernameUpdatedAt);

  useEffect(() => {
    if (open) {
      setUsername('');
      setError(null);
    }
  }, [open]);

  const reset = () => {
    setUsername('');
    setError(null);
  };

  const { requestClose, closeAfterSave } = useUnsavedChangesGuard({
    open,
    scopeKey: 'app.child',
    pending: submitting,
    dirty: username.length > 0,
    onDiscard: reset,
    onClose,
  });

  const requestDialogClose = () => {
    void requestClose();
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setError(null);
  };

  const handleSubmit = async () => {
    if (remainingCooldownDays > 0) {
      setError(
        `Username can only be changed once every ${USERNAME_COOLDOWN_DAYS} days.`,
      );
      return;
    }

    const formatError = validateUsernameFormat(username);
    if (formatError) {
      setError(formatError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      setCheckingAvailability(true);
      const avail = await usernameService.checkAvailability(username, userId);
      setCheckingAvailability(false);

      if (!avail.available) {
        setError(avail.message || 'Username is already taken.');
        setSubmitting(false);
        return;
      }

      const updated = await usernameService.changeUsername(userId, username);
      onUsernameChanged(updated);
      reset();
      closeAfterSave();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to update username.';
      setError(msg);
    } finally {
      setSubmitting(false);
      setCheckingAvailability(false);
    }
  };

  const content = (
    <>
      <DialogHeader
        id="change-username-title"
        title="Change username"
        onClose={onDismiss || requestDialogClose}
        onBack={embedded ? requestDialogClose : undefined}
      />
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Usernames must be 3–20 characters long and can be changed once every
            20 days.
          </Typography>

          {remainingCooldownDays > 0 ? (
            <Alert severity="warning" role="alert">
              Username can only be changed once every {USERNAME_COOLDOWN_DAYS}{' '}
              days. You can change your username again in{' '}
              {remainingCooldownDays} day
              {remainingCooldownDays === 1 ? '' : 's'}.
            </Alert>
          ) : (
            <TextField
              label="New username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              placeholder="e.g. alex_123"
              disabled={submitting}
              autoFocus
              inputProps={{ maxLength: 20 }}
            />
          )}

          {error && (
            <Typography color="error" role="alert">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestDialogClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={
            remainingCooldownDays > 0 ||
            !username.trim() ||
            submitting ||
            checkingAvailability
          }
        >
          {submitting ? 'Updating…' : 'Update username'}
        </Button>
      </DialogActions>
    </>
  );
  return embedded ? (
    content
  ) : (
    <ResponsiveDialog
      open={open}
      onClose={requestDialogClose}
      aria-labelledby="change-username-title"
      maxWidth="xs"
    >
      {content}
    </ResponsiveDialog>
  );
}
